/**
 * The Outcomes page. A list of every outcome capability beside a detail rail for
 * the selected one.
 */
import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import type { OutcomeTree, OverviewItem } from "../types";
import { OutcomeDetailRail } from "./OutcomeDetailRail";
import { OutcomesList } from "./OutcomesList";

interface OutcomesSectionProps {
  outcomes: OutcomeTree[];
  onOpenGraph: (item: OverviewItem) => void;
  isLoadingProductTrees: boolean;
}

export const OutcomesSection = ({
  outcomes,
  onOpenGraph,
  isLoadingProductTrees,
}: OutcomesSectionProps) => {
  const [filter, setFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const visible = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return outcomes;
    return outcomes.filter((outcome) =>
      outcome.title.toLowerCase().includes(query)
    );
  }, [outcomes, filter]);

  // Keep a row selected so the rail always has something to show.
  useEffect(() => {
    if (visible.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((current) =>
      current && visible.some((outcome) => outcome.id === current)
        ? current
        : visible[0].id
    );
  }, [visible]);

  const selected = visible.find((outcome) => outcome.id === selectedId);

  return (
    <section className="mx-auto flex h-full min-h-0 w-full max-w-7xl flex-col gap-4 px-4 py-8">
      <header className="flex flex-none items-start gap-5">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-2.5">
            <h1 className="text-[22px] font-semibold tracking-[-0.02em]">
              Outcomes
            </h1>
            <span className="rounded-full border border-unlikely/25 bg-unlikely/[0.07] px-2.5 py-0.5 text-xs font-medium text-unlikely">
              {outcomes.length}{" "}
              {outcomes.length === 1 ? "capability" : "capabilities"}
            </span>
          </div>
          <p className="max-w-[720px] text-[13px] leading-relaxed text-brand-muted">
            Each outcome decides whether one product may be suggested to this
            customer. They are re-evaluated on every turn, and the answer goes
            to the agent before it writes.
          </p>
        </div>
        <label className="flex h-[34px] w-[210px] flex-none items-center gap-2 rounded-lg border border-brand-border bg-brand-card px-2.5 focus-within:border-unlikely/40">
          <Search
            className="h-[15px] w-[15px] flex-none text-gray-400"
            aria-hidden={true}
          />
          <input
            type="search"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Search outcomes"
            aria-label="Search outcomes"
            className="min-w-0 flex-1 bg-transparent text-[13px] text-brand-foreground outline-none placeholder:text-gray-500"
          />
        </label>
      </header>

      <div className="flex min-h-0 flex-1 gap-4">
        <OutcomesList
          outcomes={visible}
          selectedId={selectedId}
          onSelect={setSelectedId}
          isLoading={isLoadingProductTrees}
          filter={filter}
        />
        {selected && (
          <OutcomeDetailRail
            outcome={selected}
            onOpenFullCapability={() => onOpenGraph(selected)}
          />
        )}
      </div>
    </section>
  );
};
